import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, resolveImage } from "@/lib/api";
import Layout from "@/components/Layout";
import { useSEO } from "@/hooks/useSEO";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, MessageSquare, Users, Palette, Pencil,
  Clock, ChevronDown, ChevronUp, Upload, Loader2, Check, X
} from "lucide-react";

const C = {
  bg: "#070707", bg2: "#11100E", card: "#181614", text: "#F5F2EB", text2: "#B5B0A7",
  gold: "#C9A055", red: "#FF3B30", bSub: "rgba(245,242,235,0.10)", bGold: "rgba(201,160,85,0.35)",
};
const F = { brush: "'Permanent Marker', cursive", cond: "'Saira Condensed', sans-serif", hand: "'Caveat', cursive", sans: "'DM Sans', 'Outfit', sans-serif" };

function useVisible(threshold = 0.15) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold });
    obs.observe(ref.current);
    const timer = setTimeout(() => setV(true), 600);
    return () => { obs.disconnect(); clearTimeout(timer); };
  }, [threshold]);
  return [ref, v];
}

const MarkerStroke = ({ color = C.gold, width = "100%", height = "6px", className = "" }) => (
  <div className={className} style={{ width, height, background: color, borderRadius: "3px", transform: "rotate(-0.8deg)", opacity: 0.85, boxShadow: `0 0 12px ${color}40` }} />
);

const BUDGET_OPTIONS = ["$300 - $500", "$500 - $1,000", "$1,000 - $2,500", "$2,500+", "Let's discuss"];

export default function MCCommissions() {
  useSEO({ title: "Custom Commissions — Marker Collection", description: "Commission a custom marker drawing. Hand-drawn, one-of-a-kind art made just for you." });
  const navigate = useNavigate();
  const [pageInfo, setPageInfo] = useState({});
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroRef, heroVis] = useVisible(0.1);
  const [processRef, processVis] = useVisible(0.1);
  const [faqRef, faqVis] = useVisible(0.1);
  const [openFaq, setOpenFaq] = useState(null);

  const [show, setShow] = useState(false);
  useEffect(() => { if (!loading) { const t = setTimeout(() => setShow(true), 100); return () => clearTimeout(t); } }, [loading]);

  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState("");
  const [referenceImages, setReferenceImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    Promise.all([
      http.get("/marker-collection/public/pages"),
      http.get("/marker-collection/public/queue"),
    ]).then(([p, q]) => {
      setPageInfo(p.data?.commissions || {});
      setQueue(q.data || []);
      if (p.data?.commissions?.enabled === false) navigate("/marker-collection");
    }).catch(() => {}).finally(() => setLoading(false));
  }, [navigate]);

  const processSteps = (pageInfo?.process || "Share Your Vision|We Plan the Details|I Draw the Magic|You Get the Original").split("|");
  const faqPairs = (pageInfo?.faq || "").split("|");
  const faqs = [];
  for (let i = 0; i < faqPairs.length - 1; i += 2) {
    faqs.push({ q: faqPairs[i], a: faqPairs[i + 1] });
  }

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (referenceImages.length + files.length > 5) { toast.error("Max 5 images"); return; }
    setUploading(true);
    for (const f of files) {
      try {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await http.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setReferenceImages(p => [...p, data.url]);
      } catch { toast.error("Upload failed"); }
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { toast.error("Name and email required"); return; }
    if (!description.trim()) { toast.error("Describe your vision"); return; }
    setSubmitting(true);
    try {
      await http.post("/marker-collection/public/commission-request", { name, email, description, budget, reference_images: referenceImages });
      setSubmitted(true);
      toast.success("Commission request submitted!");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to submit"); }
    finally { setSubmitting(false); }
  };

  const STEP_ICONS = [MessageSquare, Users, Palette, Check];

  if (loading) return (
    <Layout><div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
      <div className="w-16 h-16 border-2 animate-spin" style={{ borderColor: `${C.gold}30`, borderTopColor: C.gold }} />
    </div></Layout>
  );

  const inpStyle = { fontFamily: F.sans, background: "rgba(255,255,255,0.05)", border: `1px solid ${C.bSub}`, color: C.text };

  return (
    <Layout>
      <div style={{ background: C.bg, color: C.text, minHeight: "100vh" }}>
        <style>{`
          .mc-grit::before { content:''; position:absolute; inset:0; width:100%; height:100%; background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"); pointer-events:none; z-index:0; }
        `}</style>

        {/* Hero */}
        <section ref={heroRef} className="relative mc-grit overflow-hidden" style={{ background: C.bg, borderBottom: `1px solid ${C.bSub}` }} data-testid="mc-commissions-hero">
          <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 relative z-10">
            <Link to="/marker-collection" className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold mb-6 hover:opacity-70 transition-opacity"
              style={{ fontFamily: F.sans, color: C.gold }} data-testid="mc-commissions-back">
              <ArrowLeft size={14} /> Back to Home
            </Link>
            <div className={`transition-all duration-700 ${show || heroVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <span className="inline-block text-[10px] tracking-[0.4em] uppercase font-bold px-4 py-1.5 mb-4"
                style={{ fontFamily: F.sans, background: C.gold, color: C.bg }}>
                {pageInfo?.badge || "COMMISSIONS OPEN"}
              </span>
              <h1 className="text-5xl sm:text-6xl lg:text-[5.5rem] font-black leading-[0.85] tracking-tight mb-4" style={{ fontFamily: F.brush, color: C.text }} data-testid="mc-commissions-title">
                {pageInfo?.heading || "MAKE\nIT YOURS."}
              </h1>
              <MarkerStroke width="80px" className="mb-5" />
              <p className="text-base max-w-xl leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }}>
                {pageInfo?.text || "A portrait, a place, a memory, or an idea — I'll bring it to life, mark by mark."}
              </p>
              <p className="mt-4 text-sm" style={{ fontFamily: F.sans, color: C.text2 }}>
                Starting at <strong style={{ color: C.gold }}>${Number(pageInfo?.starting_price || 300).toLocaleString()}</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Process Steps */}
        <section ref={processRef} className="relative mc-grit" style={{ background: C.bg2, borderBottom: `1px solid ${C.bSub}` }} data-testid="mc-commissions-process">
          <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 relative z-10">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-10 text-center" style={{ fontFamily: F.cond, color: C.text }}>HOW IT WORKS</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps.map((step, i) => {
                const Icon = STEP_ICONS[i] || Palette;
                return (
                  <div key={i} className={`text-center p-6 transition-all duration-600 ${processVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                    style={{ transitionDelay: `${150 + i * 120}ms`, background: `${C.gold}05`, border: `1px solid ${C.bGold}` }}>
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center" style={{ background: C.gold }}>
                      <Icon size={24} style={{ color: C.bg }} />
                    </div>
                    <span className="text-[10px] tracking-[0.3em] uppercase font-bold block mb-2" style={{ fontFamily: F.sans, color: C.gold }}>STEP {i + 1}</span>
                    <p className="text-sm font-bold" style={{ fontFamily: F.sans, color: C.text }}>{step.trim()}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Commission Form + Queue */}
        <section className="relative mc-grit" style={{ background: C.bg }} data-testid="mc-commissions-form-section">
          <div className="max-w-7xl mx-auto px-6 py-14 md:py-20 relative z-10">
            <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16">
              {/* Form */}
              <div>
                <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-6" style={{ fontFamily: F.cond, color: C.text }}>
                  {submitted ? "REQUEST RECEIVED." : "TELL ME YOUR VISION."}
                </h2>
                {submitted ? (
                  <div className="p-8 text-center" style={{ background: `${C.gold}08`, border: `1px solid ${C.bGold}` }}>
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center" style={{ background: C.gold }}><Check size={28} style={{ color: C.bg }} /></div>
                    <p className="text-sm mb-2" style={{ fontFamily: F.sans, color: C.text }}>Mickey will review your vision within 48 hours.</p>
                    <p className="text-xs" style={{ fontFamily: F.sans, color: C.text2 }}>Keep an eye on your email for updates.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-4" data-testid="mc-commissions-form">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase block mb-1.5" style={{ fontFamily: F.sans, color: `${C.gold}90` }}>Your Name *</label>
                        <input className="w-full px-4 py-2.5 text-sm focus:outline-none" style={inpStyle} value={name} onChange={e => setName(e.target.value)} required data-testid="mc-commissions-name" />
                      </div>
                      <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase block mb-1.5" style={{ fontFamily: F.sans, color: `${C.gold}90` }}>Email *</label>
                        <input type="email" className="w-full px-4 py-2.5 text-sm focus:outline-none" style={inpStyle} value={email} onChange={e => setEmail(e.target.value)} required data-testid="mc-commissions-email" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.2em] uppercase block mb-1.5" style={{ fontFamily: F.sans, color: `${C.gold}90` }}>What's Your Vision? *</label>
                      <textarea className="w-full px-4 py-2.5 text-sm focus:outline-none min-h-[120px] resize-y" style={inpStyle}
                        placeholder="A portrait, a scene, a concept..." value={description} onChange={e => setDescription(e.target.value)} required data-testid="mc-commissions-desc" />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.2em] uppercase block mb-1.5" style={{ fontFamily: F.sans, color: `${C.gold}90` }}>Budget Range</label>
                      <div className="flex flex-wrap gap-2">
                        {BUDGET_OPTIONS.map(opt => (
                          <button key={opt} type="button" onClick={() => setBudget(budget === opt ? "" : opt)}
                            className="text-xs px-3 py-1.5 transition-all duration-200"
                            style={{ fontFamily: F.sans, border: `2px solid ${budget === opt ? C.gold : C.bSub}`, background: budget === opt ? `${C.gold}15` : "transparent", color: budget === opt ? C.gold : C.text2 }}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.2em] uppercase block mb-1.5" style={{ fontFamily: F.sans, color: `${C.gold}90` }}>Reference Images</label>
                      {referenceImages.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {referenceImages.map((img, i) => (
                            <div key={i} className="relative w-16 h-16 overflow-hidden group" style={{ border: `1px solid ${C.bSub}` }}>
                              <img src={resolveImage(img)} alt="" className="w-full h-full object-cover" />
                              <button type="button" onClick={() => setReferenceImages(p => p.filter((_, j) => j !== i))}
                                className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><X size={14} className="text-white" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                      {referenceImages.length < 5 && (
                        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                          className="flex items-center gap-2 text-xs border-2 border-dashed px-4 py-3 w-full justify-center transition-colors"
                          style={{ fontFamily: F.sans, borderColor: C.bSub, color: C.text2 }}>
                          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                          {uploading ? "Uploading..." : "Upload Reference Images"}
                        </button>
                      )}
                      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
                    </div>
                    <button type="submit" disabled={submitting}
                      className="w-full py-4 text-xs tracking-[0.25em] uppercase font-bold flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
                      style={{ fontFamily: F.sans, background: C.gold, color: C.bg }} data-testid="mc-commissions-submit">
                      {submitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                      {submitting ? "SUBMITTING..." : (pageInfo?.cta_label || "SUBMIT COMMISSION REQUEST")}
                    </button>
                  </form>
                )}
              </div>

              {/* Queue */}
              <div>
                <div className="p-6 border" style={{ borderColor: C.bSub, background: C.card }}>
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h3 className="text-2xl font-black tracking-tight" style={{ fontFamily: F.cond, color: C.text }}>THE QUEUE</h3>
                      <p className="text-[10px] tracking-[0.25em] uppercase" style={{ fontFamily: F.sans, color: C.text2 }}>CURRENT PROJECTS</p>
                    </div>
                    <div className="w-10 h-10 flex items-center justify-center" style={{ background: C.gold }}>
                      <span className="text-lg font-black" style={{ fontFamily: F.cond, color: C.bg }}>{queue.length}</span>
                    </div>
                  </div>
                  {queue.length === 0 ? (
                    <div className="py-10 text-center">
                      <p className="text-sm" style={{ fontFamily: F.sans, color: C.text2 }}>Queue is open — be the first.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {queue.map((item, i) => {
                        const isActive = item.status === "in_progress";
                        return (
                          <div key={item.id} className="flex items-center gap-3 px-3 py-3 border-b" style={{ borderColor: C.bSub }}>
                            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-sm font-black"
                              style={{ fontFamily: F.cond, background: isActive ? C.gold : "rgba(255,255,255,0.05)", color: isActive ? C.bg : C.text2 }}>
                              {isActive ? <Pencil size={14} /> : i + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm truncate" style={{ fontFamily: F.sans, color: C.text }}>{item.title}</p>
                              <p className="text-[9px] tracking-[0.15em] uppercase" style={{ fontFamily: F.sans, color: isActive ? C.gold : C.text2 }}>
                                {item.type === "commission" ? "Custom Commission" : "Personal Project"}
                              </p>
                            </div>
                            <span className="text-[8px] tracking-[0.2em] uppercase font-bold px-3 py-1.5 flex-shrink-0 flex items-center gap-1.5"
                              style={{ fontFamily: F.sans, background: isActive ? `${C.gold}15` : "rgba(255,255,255,0.03)", color: isActive ? C.gold : C.text2, border: `1px solid ${isActive ? C.bGold : C.bSub}` }}>
                              {isActive && <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.gold }} />}
                              {isActive ? "IN PROGRESS" : "UP NEXT"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <p className="mt-5 text-lg" style={{ fontFamily: F.hand, color: `${C.gold}60` }}>Good things take time.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        {faqs.length > 0 && (
          <section ref={faqRef} className="relative mc-grit" style={{ background: C.bg2, borderTop: `1px solid ${C.bSub}` }} data-testid="mc-commissions-faq">
            <div className="max-w-3xl mx-auto px-6 py-14 md:py-20 relative z-10">
              <h2 className={`text-3xl sm:text-4xl font-black tracking-tight mb-8 text-center transition-all duration-700 ${faqVis ? "opacity-100" : "opacity-0"}`}
                style={{ fontFamily: F.cond, color: C.text }}>COMMON QUESTIONS</h2>
              <div className="space-y-2">
                {faqs.map((faq, i) => (
                  <div key={i} className="border" style={{ borderColor: C.bSub, background: openFaq === i ? `${C.gold}05` : "transparent" }}>
                    <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-5 py-4 text-left"
                      data-testid={`mc-commissions-faq-${i}`}>
                      <span className="text-sm font-bold pr-4" style={{ fontFamily: F.sans, color: C.text }}>{faq.q}</span>
                      {openFaq === i ? <ChevronUp size={16} style={{ color: C.gold }} /> : <ChevronDown size={16} style={{ color: C.text2 }} />}
                    </button>
                    {openFaq === i && (
                      <div className="px-5 pb-4">
                        <p className="text-sm leading-relaxed" style={{ fontFamily: F.sans, color: C.text2 }}>{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}
